// src/hooks/useAuth.js - ARREGLADO para evitar deadlock de Supabase
import { useState, useEffect, createContext, useContext } from 'react';
import supabase from '../supabase';
import authService from '../services/auth/authService';
import authHelper from '../utils/authHelper';

// Crear contexto
const AuthContext = createContext();

// Logger mejorado
const logger = {
  info: (msg, data) => console.log(`ℹ️ [useAuth] ${msg}`, data || ''),
  success: (msg, data) => console.log(`✅ [useAuth] ${msg}`, data || ''),
  warn: (msg, data) => console.warn(`⚠️ [useAuth] ${msg}`, data || ''),
  error: (msg, error) => console.error(`❌ [useAuth] ${msg}`, error || ''),
  debug: (msg, data) => console.log(`🔍 [useAuth] DEBUG: ${msg}`, data || '')
};

// Provider del contexto
export const AuthProvider = ({ children }) => {
  // Estados principales
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);

  // ✅ FUNCIÓN: Limpiar estado de autenticación
  const clearAuthState = async () => {
    logger.info('Limpiando estado de autenticación');
    
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    setIsInitializing(false);
    
    // Limpiar indicadores locales
    authHelper.clearAllSessionIndicators();
    
    return null;
  };

  // ✅ FUNCIÓN CRÍTICA: Verificar usuario SOLO para login manual (sin timeout problemático)
  const checkUser = async (forceRefresh = false) => {
    try {
      logger.info('Iniciando checkUser para login manual', { forceRefresh });
      
      if (forceRefresh) {
        logger.debug('Refrescando sesión...');
        await supabase.refreshSession();
      }

      // Obtener sesión actual
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logger.error('Error obteniendo sesión', sessionError);
        return await clearAuthState();
      }

      if (!session || !session.user) {
        logger.warn('No hay sesión activa');
        return await clearAuthState();
      }

      logger.debug('Sesión encontrada, obteniendo perfil completo');
      
      // Obtener perfil completo
      const fullUser = await supabase.getFullUserProfile();
      
      if (!fullUser) {
        logger.error('No se pudo obtener el perfil del usuario');
        return await clearAuthState();
      }

      // ✅ ACTUALIZAR ESTADO DE FORMA ATÓMICA
      setUser(fullUser);
      setIsAuthenticated(true);
      setLoading(false);
      setIsInitializing(false);

      logger.success('Usuario autenticado correctamente', {
        id: fullUser.id,
        email: fullUser.email,
        userType: fullUser.user_type,
        isAdmin: fullUser.is_admin
      });

      return fullUser;

    } catch (error) {
      logger.error('Error en checkUser', error);
      return await clearAuthState();
    }
  };

  // ✅ FUNCIÓN DE LOGIN
  const login = async (email, password) => {
    try {
      logger.info('=== INICIANDO LOGIN ===', { email });
      
      setLoading(true);

      // Llamar al servicio de autenticación
      const authResult = await authService.login(email, password);
      
      if (!authResult || !authResult.user) {
        throw new Error('No se recibió respuesta válida del servicio');
      }

      logger.success('AuthService completado, verificando usuario...');

      // ✅ CRÍTICO: Verificar usuario inmediatamente después del login
      const fullUser = await checkUser(true); // Force refresh
      
      if (!fullUser) {
        throw new Error('No se pudo verificar el usuario después del login');
      }

      logger.success('Login completado exitosamente', {
        userId: fullUser.id,
        userType: fullUser.user_type,
        isAdmin: fullUser.is_admin
      });

      return {
        success: true,
        user: fullUser,
        redirectPath: authHelper.getRedirectPathForUser(fullUser)
      };

    } catch (error) {
      logger.error('Error en login', error);
      await clearAuthState();
      
      return {
        success: false,
        error: error.message || 'Error al iniciar sesión'
      };
    }
  };

  // ✅ FUNCIÓN DE REGISTRO
  const register = async (userData) => {
    try {
      logger.info('=== INICIANDO REGISTRO ===', { email: userData.email });
      
      setLoading(true);

      const result = await authService.register(userData);
      
      if (!result.success) {
        setLoading(false);
        return result;
      }

      logger.success('Registro completado', result);
      setLoading(false);
      
      return result;

    } catch (error) {
      logger.error('Error en registro', error);
      setLoading(false);
      
      return {
        success: false,
        error: error.message || 'Error al registrar usuario'
      };
    }
  };

  // ✅ FUNCIÓN DE LOGOUT
  const logout = async () => {
    try {
      logger.info('Iniciando logout...');
      
      setLoading(true);
      
      await authService.logout();
      await clearAuthState();
      
      logger.success('Logout completado');
      
      return { success: true };

    } catch (error) {
      logger.error('Error en logout', error);
      await clearAuthState(); // Limpiar de todas formas
      
      return { success: false, error: error.message };
    }
  };

  // ✅ EFECTO DE INICIALIZACIÓN - SIMPLIFICADO PARA EVITAR DEADLOCK
  useEffect(() => {
    let mounted = true;
    let initTimeout;

    const initializeAuth = async () => {
      try {
        logger.info('=== INICIALIZANDO AUTENTICACIÓN ===');
        
        // ✅ TIMEOUT DE SEGURIDAD más corto
        initTimeout = setTimeout(() => {
          if (mounted) {
            logger.error('🚨 TIMEOUT: Inicialización tomó demasiado tiempo - continuando sin sesión');
            setIsInitializing(false);
            setLoading(false);
          }
        }, 5000); // Reducido a 5 segundos
        
        // ✅ VERIFICACIÓN DIRECTA de localStorage SIN llamar a Supabase
        const supabaseKey = `sb-${process.env.REACT_APP_SUPABASE_URL.replace('https://', '').split('.')[0]}-auth-token`;
        const localStorageSession = localStorage.getItem(supabaseKey);
        
        if (!localStorageSession) {
          logger.info('No hay datos en localStorage');
          if (mounted) {
            setIsInitializing(false);
            setLoading(false);
          }
          return;
        }

        try {
          const parsedSession = JSON.parse(localStorageSession);
          
          if (parsedSession && parsedSession.user && parsedSession.access_token) {
            logger.info('Sesión encontrada en localStorage, dejando que onAuthStateChange la procese');
            // NO hacer nada más - dejar que onAuthStateChange se encargue
          } else {
            logger.warn('Sesión en localStorage incompleta');
            if (mounted) {
              setIsInitializing(false);
              setLoading(false);
            }
          }
        } catch (parseError) {
          logger.error('Error parseando localStorage', parseError);
          if (mounted) {
            setIsInitializing(false);
            setLoading(false);
          }
        }

      } catch (error) {
        logger.error('Error en inicialización', error);
        if (mounted) {
          setIsInitializing(false);
          setLoading(false);
        }
      } finally {
        if (initTimeout) {
          clearTimeout(initTimeout);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
    };
  }, []);

  // ✅ LISTENER DE CAMBIOS DE AUTENTICACIÓN - SOLUCIÓN DEFINITIVA AL DEADLOCK
  useEffect(() => {
    let mounted = true;
    logger.debug('Configurando listener de auth state change', { mounted });
    
   // Cambiar SOLO esta parte en el listener de useAuth.js:

// Cambiar SOLO esta parte en el listener de useAuth.js:

const { data: { subscription } } = supabase.auth.onAuthStateChange(
  (event, session) => {
    logger.info('Auth state change detectado', { event, hasSession: !!session, mounted });
    
    if (event === 'SIGNED_OUT' || !session) {
      logger.info('Limpiando estado por SIGNED_OUT');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      setIsInitializing(false);
      authHelper.clearAllSessionIndicators();
    } else if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user && !isAuthenticated) {
      logger.info('Sesión detectada, cargando perfil...');
      
      // ✅ CARGAR PERFIL EN BACKGROUND SIN BLOQUEAR
      setTimeout(async () => {
        try {
          const fullUser = await supabase.getFullUserProfile();
          if (fullUser && mounted) {
            setUser(fullUser);
            setIsAuthenticated(true);
            setLoading(false);
            setIsInitializing(false);
            
            logger.success('✅ Usuario autenticado con perfil completo', {
              companyName: fullUser.company_name,
              memberNumber: fullUser.member_number,
              membershipLevel: fullUser.membership_level || fullUser.subscription_tier,
              logo: fullUser.logo ? 'SÍ' : 'NO'
            });
          } else {
            logger.warn('No se obtuvo perfil completo');
            setLoading(false);
            setIsInitializing(false);
          }
        } catch (error) {
          logger.error('Error obteniendo perfil completo', error);
          setLoading(false);
          setIsInitializing(false);
        }
      }, 100); // ✅ Delay mínimo para no bloquear el listener
    }
    // ✅ Ignorar TOKEN_REFRESHED completamente
  }
);

    return () => {
      mounted = false;
      logger.debug('Limpiando listener de auth state change, desmontando...', { mounted });
      subscription.unsubscribe();
    };
  }, [isAuthenticated]);

// En useAuth.js - Agregar esta función antes del contextValue:

// ✅ FUNCIÓN PARA ACTUALIZAR PERFIL
const updateProfile = async (userData) => {
  try {
    logger.info('Actualizando perfil de usuario...', userData);
    
    // Actualizar metadatos en Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.updateUser({
      data: userData
    });
    
    if (authError) {
      logger.error('Error actualizando auth user', authError);
      throw authError;
    }
    
    // Actualizar el perfil en la tabla correspondiente
    if (user) {
      const isCompany = user.user_type === 'company' || user.is_company;
      const tableName = isCompany ? 'company_profiles' : 'user_profiles';
      const identifier = isCompany ? 'email' : 'id';
      const identifierValue = isCompany ? user.email : user.id;
      
      const { error: profileError } = await supabase
        .from(tableName)
        .update({
          ...userData,
          updated_at: new Date().toISOString()
        })
        .eq(identifier, identifierValue);
      
      if (profileError) {
        logger.error('Error actualizando perfil en tabla', profileError);
        // No hacer throw aquí, ya que los metadatos ya se actualizaron
      }
      
      // Actualizar el estado local del usuario
      setUser(prevUser => ({
        ...prevUser,
        ...userData,
        user_metadata: {
          ...prevUser.user_metadata,
          ...userData
        }
      }));
      
      logger.success('Perfil actualizado correctamente');
    }
    
    return { success: true };
    
  } catch (error) {
    logger.error('Error en updateProfile', error);
    return { success: false, error: error.message };
  }
};


  

  // ✅ FUNCIÓN: Solicitar recuperación de contraseña (envía email vía Resend)
  const forgotPassword = async (email) => {
    logger.info('Solicitando recuperación de contraseña', { email });
    return authService.forgotPassword(email);
  };

  // ✅ FUNCIÓN: Establecer nueva contraseña (desde el link del email)
  const resetPassword = async (newPassword) => {
    logger.info('Restableciendo contraseña');
    return authService.resetPassword(newPassword);
  };

  // ✅ FUNCIÓN: OAuth con Google. Si userType viene, lo propaga al callback.
  const signInWithGoogle = async (userType = null) => {
    logger.info('Solicitando OAuth Google', { userType });
    return authService.signInWithGoogle(userType);
  };

  // Valor del contexto
  const contextValue = {
    user,
    isAuthenticated,
    loading,
    isInitializing,
    login,
    register,
    logout,
    checkUser,
    clearAuthState,
    updateProfile,
    forgotPassword,
    resetPassword,
    signInWithGoogle,
    supabase
    };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  
  return context;
};